import React from 'react';
import { useSelector } from 'react-redux';

import { type SortingState, flexRender, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { cva } from 'class-variance-authority';
import { ArrowUpDown } from 'lucide-react';

import { InnerChildSequenceTableColumns } from './InnerChildSequenceTableColumns';
import type { RegionNumbering } from '../../../generated/generatedOpenAPI';
import { selectHighlightedRegions, selectSearchQuery, selectShowFWR, selectShowLiabilities, selectShowSHM } from '../../../store/slices/antibodyAnalysis.slice';
import { useChainWidths } from '../../context/ChainWidthContext';
import type { SequenceData } from '../../types';
import type { SequenceRow } from '../../types/antibodyTable';
import { frozenCellStyle } from '../../utilities/frozenColumn';

const s = {
  container: '',
  table: 'border-collapse table-fixed',
  thead: 'sticky z-[15] bg-[#e2f2ff]',
  headerCell: 'text-left text-[13px] font-medium text-[#3d3d3d] px-2 py-1.5 border-b border-border/40',
  headerInner: 'flex items-center gap-1',
  headerSortable: 'flex items-center gap-1 cursor-pointer select-none hover:text-foreground/80',
  sortIcon: 'opacity-40 translate-y-px',
  sortIconSvg: 'w-3 h-3',
  bodyCell: 'px-2 py-0 text-xs align-middle bg-[#e2f2ff]',
} as const;

// The inner table's first two columns (checkbox + Node/barcode) are frozen left so the sequence's
// identity stays visible on horizontal scroll.
const INNER_PINNED_COUNT = 2;
const Z_INDEX_INNER_HEADER = 1;
const Z_INDEX_INNER_BODY = 11;
const INNER_ROW_BG = '#e2f2ff';
const INNER_ROW_BG_SELECTED = '#d0e8ff';
const innerPinnedLeft = (offset: number) => `calc(var(--pinned-left-w, 162px) + ${offset}px)`;

const sequenceRowStyle = cva('border-b border-border/30 bg-[#e2f2ff]', {
  variants: {
    selected: { true: 'bg-[#d0e8ff]', false: 'hover:bg-[#d6edff]' },
  },
  defaultVariants: { selected: false },
});

interface InnerChildSequenceTableProps {
  clonotypeId: number;
  sequences: SequenceData[];
  isSequenceSelected: (clonotypeId: number, sequenceId: string) => boolean;
  onSequenceToggle: (clonotypeId: number, sequenceId: string) => void;
  onRegionClick: (region: string | null) => void;
  chainType: 'all' | 'heavy' | 'light';
  heavyRegions?: RegionNumbering[];
  lightRegions?: RegionNumbering[] | null;
}

export function InnerChildSequenceTable({
  clonotypeId,
  sequences,
  isSequenceSelected,
  onSequenceToggle,
  onRegionClick,
  chainType,
  heavyRegions,
  lightRegions,
}: InnerChildSequenceTableProps) {
  const showFWR = useSelector(selectShowFWR);
  const highlightedRegions = useSelector(selectHighlightedRegions);
  const searchQuery = useSelector(selectSearchQuery);
  const showLiabilities = useSelector(selectShowLiabilities);
  const showShms = useSelector(selectShowSHM);
  // Global chain widths come from context (computed once across all clonotypes) rather
  // than props, so a showFWR/highlightedRegions toggle re-renders only these sequence
  // tables — not the whole OuterParentClonotypeTable.
  const { heavy: globalHeavyChainWidth, light: globalLightChainWidth } = useChainWidths();

  const [sorting, setSorting] = React.useState<SortingState>([]);

  const data: SequenceRow[] = React.useMemo(
    () =>
      [...sequences]
        .sort((a, b) => (b.metadata.aps ?? 0) - (a.metadata.aps ?? 0))
        .map((seq) => {
          const { barcodes } = seq.metadata;
          const nodeLabel = barcodes.length > 1 ? `${barcodes[0]} (+${barcodes.length - 1} more)` : barcodes[0] || seq.metadata.sequenceId;
          return {
            clonotypeId,
            sequenceId: seq.metadata.sequenceId,
            nodeLabel,
            nodeSize: barcodes.length,
            aps: seq.metadata.aps ?? 0,
            liabilityScore: seq.metadata.liabilityScore ?? 0,
            shms: seq.metadata.shms ?? 0,
            sequenceData: seq,
          };
        }),
    [clonotypeId, sequences],
  );

  const columns = React.useMemo(
    () =>
      InnerChildSequenceTableColumns({
        clonotypeId,
        sequences,
        isSequenceSelected,
        onSequenceToggle,
        onRegionClick,
        chainType,
        searchQuery,
        showFWR,
        highlightedRegions,
        showLiabilities,
        showShms,
        heavyChainWidth: globalHeavyChainWidth,
        lightChainWidth: globalLightChainWidth,
        heavyRegions,
        lightRegions,
      }),
    [
      clonotypeId,
      sequences,
      isSequenceSelected,
      onSequenceToggle,
      onRegionClick,
      chainType,
      searchQuery,
      showFWR,
      highlightedRegions,
      showLiabilities,
      showShms,
      globalHeavyChainWidth,
      globalLightChainWidth,
      heavyRegions,
      lightRegions,
    ],
  );

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => `inner_${row.clonotypeId}_${row.sequenceId}`,
  });

  return (
    <div className={s.container}>
      <table className={s.table} style={{ width: '100%', minWidth: table.getTotalSize() }}>
        <colgroup>
          {table.getVisibleLeafColumns().map((col) => (
            <col key={col.id} style={{ width: col.getSize() }} />
          ))}
          <col />
        </colgroup>
        {/* useLayoutEffect sets --outer-header-h before paint, so no fallback is needed. */}
        <thead className={s.thead} style={{ top: 'var(--outer-header-h)' }}>
          <tr>
            {(table.getHeaderGroups()[0]?.headers ?? []).map((header, hIdx, headers) => {
              const isPinned = hIdx < INNER_PINNED_COUNT;
              // Only the pinned cells need a running offset — skip the reduce for the rest.
              const offset = isPinned ? headers.slice(0, hIdx).reduce((sum, h) => sum + h.getSize(), 0) : 0;
              return (
                <th
                  key={header.id}
                  className={s.headerCell}
                  style={{
                    width: header.getSize(),
                    ...(isPinned ? frozenCellStyle(innerPinnedLeft(offset), hIdx === INNER_PINNED_COUNT - 1, Z_INDEX_INNER_HEADER, INNER_ROW_BG) : {}),
                  }}
                >
                  <div className={header.column.getCanSort() ? s.headerSortable : s.headerInner} onClick={header.column.getToggleSortingHandler()}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getCanSort() && (
                      <span className={s.sortIcon}>
                        <ArrowUpDown className={s.sortIconSvg} />
                      </span>
                    )}
                  </div>
                </th>
              );
            })}
            <th aria-hidden className={s.headerCell} />
          </tr>
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => {
            const selected = isSequenceSelected(row.original.clonotypeId, row.original.sequenceId);
            return (
              <tr key={row.id} className={sequenceRowStyle({ selected })}>
                {row.getVisibleCells().map((cell, cellIdx, cells) => {
                  const isPinned = cellIdx < INNER_PINNED_COUNT;
                  // Only the pinned cells need a running offset — skip the reduce for the rest.
                  const offset = isPinned ? cells.slice(0, cellIdx).reduce((sum, c) => sum + c.column.getSize(), 0) : 0;
                  return (
                    <td
                      key={cell.id}
                      className={s.bodyCell}
                      style={{
                        width: cell.column.getSize(),
                        ...(isPinned
                          ? frozenCellStyle(
                              innerPinnedLeft(offset),
                              cellIdx === INNER_PINNED_COUNT - 1,
                              Z_INDEX_INNER_BODY,
                              selected ? INNER_ROW_BG_SELECTED : INNER_ROW_BG,
                            )
                          : {}),
                      }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  );
                })}
                <td aria-hidden className={s.bodyCell} />
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
